/**
 * Página "Ayuda" para jugadores
 * 
 * Muestra:
 * - Preguntas frecuentes
 * - Guías de uso
 * - Información de contacto
 * - Soporte técnico
 */

import { useState, useEffect } from 'react';

const PlayerAyuda = () => {
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const containerStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const cardStyle = {
    backgroundColor: 'white',
    borderRadius: '15px',
    padding: window.innerWidth <= 768 ? '20px' : '30px',
    marginBottom: '25px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid #E8ECFF'
  };

  const titleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.8rem' : '2.2rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '30px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px'
  };

  const sectionTitleStyle = {
    fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem',
    color: '#061156',
    fontWeight: '700',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const faqData = [
    {
      id: 1,
      question: "¿Cómo funcionan los ProPoints™?",
      answer: "Los ProPoints™ son un sistema de puntuación que evalúa tu rendimiento en cuatro áreas: técnica, táctica, física y mental. Se otorgan después de cada jornada basándose en tu desempeño y actitud deportiva."
    },
    {
      id: 2,
      question: "¿Cómo puedo ver mis próximas jornadas?",
      answer: "Puedes ver tus próximas jornadas en la sección 'Jornadas' del menú principal. Allí encontrarás fechas, horarios y ubicaciones de tus próximos partidos."
    },
    {
      id: 3,
      question: "¿Puedo cambiar mi información personal?",
      answer: "Por seguridad, los jugadores no pueden modificar directamente su información personal. Para realizar cambios, contacta con tu tutor o el administrador del sistema."
    },
    {
      id: 4,
      question: "¿Cómo se calcula mi ranking?",
      answer: "Tu ranking se basa en tus ProPoints™ acumulados durante la temporada actual, comparándote con otros jugadores de tu división y categoría."
    },
    {
      id: 5,
      question: "¿Qué hago si no recibo notificaciones?",
      answer: "Verifica que tengas notificaciones habilitadas en tu navegador. Si el problema persiste, contacta con soporte técnico."
    },
    {
      id: 6,
      question: "¿Cómo puedo mejorar mis evaluaciones?",
      answer: "Enfócate en entrenar las cuatro áreas: técnica (control y pases), táctica (posicionamiento), física (condición) y mental (actitud y trabajo en equipo)."
    }
  ];

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>
        ❓ Centro de Ayuda
      </h1>

      {/* Acceso Rápido */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          🚀 Acceso Rápido
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(3, 1fr)',
          gap: '20px'
        }}>
          <div style={{
            backgroundColor: '#E8F5E8',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #4CAF50',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '10px'
            }}>
              📚
            </div>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#2E7D32',
              margin: '0 0 8px 0'
            }}>
              Guía de Inicio
            </h4>
            <p style={{
              fontSize: '0.9rem',
              color: '#2E7D32',
              margin: 0
            }}>
              Aprende a usar la plataforma
            </p>
          </div>
          
          <div style={{
            backgroundColor: '#E3F2FD',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #2196F3',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '10px'
            }}>
              💬
            </div>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#1565C0',
              margin: '0 0 8px 0'
            }}>
              Contactar Soporte
            </h4>
            <p style={{
              fontSize: '0.9rem',
              color: '#1565C0',
              margin: 0
            }}>
              Obtén ayuda personalizada
            </p>
          </div>
          
          <div style={{
            backgroundColor: '#FFF3E0',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #FF9800',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              fontSize: '2rem',
              marginBottom: '10px'
            }}>
              🎯
            </div>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#E65100',
              margin: '0 0 8px 0'
            }}>
              Consejos ProPoints
            </h4>
            <p style={{
              fontSize: '0.9rem',
              color: '#E65100',
              margin: 0
            }}>
              Mejora tu rendimiento
            </p>
          </div>
        </div>
      </div>

      {/* Preguntas Frecuentes */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          ❓ Preguntas Frecuentes
        </h2>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}>
          {faqData.map((faq) => (
            <div 
              key={faq.id}
              style={{
                backgroundColor: '#F8F9FF',
                borderRadius: '12px',
                border: '2px solid #E8ECFF',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  padding: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: activeSection === faq.id ? '#E8ECFF' : 'transparent'
                }}
                onClick={() => toggleSection(faq.id)}
              >
                <h4 style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#061156',
                  margin: 0
                }}>
                  {faq.question}
                </h4>
                <span style={{
                  fontSize: '1.2rem',
                  color: '#4352A9',
                  transform: activeSection === faq.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}>
                  ▼
                </span>
              </div>
              
              {activeSection === faq.id && (
                <div style={{
                  padding: '0 20px 20px 20px',
                  backgroundColor: 'white'
                }}>
                  <p style={{
                    fontSize: '1rem',
                    color: '#666',
                    margin: 0,
                    lineHeight: '1.6'
                  }}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Información de Contacto */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          📞 Información de Contacto
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
          gap: '25px'
        }}>
          <div style={{
            backgroundColor: '#E8F5E8',
            padding: '25px',
            borderRadius: '12px',
            border: '2px solid #4CAF50'
          }}>
            <h4 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              color: '#2E7D32',
              margin: '0 0 15px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              👨‍🏫 Tu Tutor
            </h4>
            <p style={{
              fontSize: '1rem',
              color: '#2E7D32',
              margin: '0 0 10px 0'
            }}>
              Para consultas sobre tu rendimiento, evaluaciones y desarrollo deportivo.
            </p>
            <p style={{
              fontSize: '0.9rem',
              color: '#2E7D32',
              margin: 0,
              fontStyle: 'italic'
            }}>
              Contacta directamente con tu tutor asignado durante las jornadas.
            </p>
          </div>
          
          <div style={{
            backgroundColor: '#E3F2FD',
            padding: '25px',
            borderRadius: '12px',
            border: '2px solid #2196F3'
          }}>
            <h4 style={{
              fontSize: '1.2rem',
              fontWeight: '700',
              color: '#1565C0',
              margin: '0 0 15px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              🛠️ Soporte Técnico
            </h4>
            <p style={{
              fontSize: '1rem',
              color: '#1565C0',
              margin: '0 0 10px 0'
            }}>
              Para problemas técnicos, acceso a la plataforma o dudas sobre el sistema.
            </p>
            <p style={{
              fontSize: '0.9rem',
              color: '#1565C0',
              margin: 0,
              fontStyle: 'italic'
            }}>
              📧 soporte@prokidsapp.com
            </p>
          </div>
        </div>
      </div>

      {/* Consejos y Trucos */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>
          💡 Consejos y Trucos
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
          gap: '20px'
        }}>
          <div style={{
            backgroundColor: '#FFF3E0',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #FF9800'
          }}>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#E65100',
              margin: '0 0 10px 0'
            }}>
              🎯 Maximiza tus ProPoints
            </h4>
            <ul style={{
              fontSize: '0.9rem',
              color: '#E65100',
              margin: 0,
              paddingLeft: '20px'
            }}>
              <li>Mantén una actitud positiva</li>
              <li>Escucha a tu tutor</li>
              <li>Trabaja en equipo</li>
              <li>Practica regularmente</li>
            </ul>
          </div>
          
          <div style={{
            backgroundColor: '#F3E5F5',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid #9C27B0'
          }}>
            <h4 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#6A1B9A',
              margin: '0 0 10px 0'
            }}>
              📱 Usa la plataforma
            </h4>
            <ul style={{
              fontSize: '0.9rem',
              color: '#6A1B9A',
              margin: 0,
              paddingLeft: '20px'
            }}>
              <li>Revisa tus notificaciones</li>
              <li>Consulta tu progreso</li>
              <li>Mantente informado</li>
              <li>Contacta si tienes dudas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerAyuda;
